export class NoiseOverlay {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private frameCount = 0;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d')!;
  }

  resize(w: number, h: number): void {
    this.canvas.width = w;
    this.canvas.height = h;
  }

  draw(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    this.frameCount++;
    if (this.frameCount % 3 !== 0) return;

    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.resize(w, h);
    }

    const imageData = this.ctx.createImageData(w, h);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 16) {
      const v = Math.random() * 255;
      data[i] = v;
      data[i + 1] = v;
      data[i + 2] = v;
      data[i + 3] = 8;
    }
    this.ctx.putImageData(imageData, 0, 0);

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.drawImage(this.canvas, 0, 0);
    ctx.restore();
  }
}

export class WavesBackground {
  private t = 0;

  draw(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    amplitude: number,
    accentRgb: string,
  ): void {
    this.t += 0.008;
    const layers = 4;

    for (let layer = 0; layer < layers; layer++) {
      ctx.beginPath();
      const layerY = h * 0.3 + layer * h * 0.1;
      ctx.moveTo(0, h);

      for (let x = 0; x <= w; x += 3) {
        const wave1 = Math.sin(x * 0.004 + this.t * (0.6 + layer * 0.2)) * (20 + amplitude * 40);
        const wave2 = Math.sin(x * 0.008 + this.t * 1.1 + layer * 0.7) * (10 + amplitude * 15);
        const wave3 = Math.cos(x * 0.002 + this.t * 0.4) * 15;
        const y = layerY + wave1 + wave2 + wave3;
        ctx.lineTo(x, y);
      }

      ctx.lineTo(w, h);
      ctx.closePath();

      const alpha = 0.03 + amplitude * 0.06 + layer * 0.01;
      ctx.fillStyle = `rgba(${accentRgb}, ${alpha})`;
      ctx.fill();
    }
  }
}

interface SparkParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
}

export class ClickSparkSystem {
  private sparks: SparkParticle[] = [];

  emit(x: number, y: number, color: string, count: number = 8): void {
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4;
      const speed = Math.random() * 3 + 1.5;
      this.sparks.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 30 + Math.random() * 20,
        maxLife: 50,
        size: Math.random() * 2.5 + 0.5,
        color,
      });
    }
  }

  update(): void {
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i];
      s.x += s.vx;
      s.y += s.vy;
      s.vx *= 0.96;
      s.vy *= 0.96;
      s.life--;
      if (s.life <= 0) this.sparks.splice(i, 1);
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const s of this.sparks) {
      const alpha = (s.life / s.maxLife) * 0.9;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.size * (s.life / s.maxLife), 0, Math.PI * 2);
      ctx.fillStyle = s.color;
      ctx.globalAlpha = alpha;
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
}

interface ShockwaveRing {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  alpha: number;
  color: string;
}

export class ShockwaveSystem {
  private rings: ShockwaveRing[] = [];

  emit(x: number, y: number, color: string, maxRadius: number = 90): void {
    this.rings.push({
      x,
      y,
      radius: 12,
      maxRadius,
      alpha: 0.55,
      color,
    });
  }

  update(): void {
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i];
      r.radius += (r.maxRadius - r.radius) * 0.12 + 0.6;
      r.alpha *= 0.91;
      if (r.alpha < 0.01 || r.radius >= r.maxRadius - 2) {
        this.rings.splice(i, 1);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const r of this.rings) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(r.x, r.y, r.radius, 0, Math.PI * 2);
      ctx.strokeStyle = r.color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = r.alpha;
      ctx.stroke();
      ctx.restore();
    }
  }
}
